import { useEffect, useMemo, useRef, useState } from "react";
import { useConnection } from "wagmi";
import { pt, type Instrument, type Scenario, type TicketPreview } from "../../sdk";
import { AmountInput, Button, Segmented } from "../../components/ui";
import { ConnectButton } from "../../wallet/ConnectButton";
import { navigate } from "../../router";
import { useAccount, useOwner, useStreamStatus } from "../../state/account";
import { enableTrading, openPosition, useInvalidateKey, useKeyState } from "../../state/actions";
import { useMark, useSummary } from "../../state/market";
import { useStore } from "../../state/store";
import { REFUSAL } from "../../lib/errors";
import { big, fmt, fromRaw, paper, pctPpm, pnl, price, scalePlaces, tone, usd } from "../../lib/num";
import {
  fromSlider,
  leverageFor,
  presets,
  preview as previewStore,
  rememberLeverage,
  setTicket,
  ticket as ticketStore,
  toSlider,
  type Side,
} from "./ticket-state";

const CENT = 10n ** 16n;

/** The ladder of moves: the bust's own distance, against and for the position, so every
 * leverage reads the same way: what the move that would bust you pays the other way. */
function ladder(bustPpm: string | null | undefined, isLong: boolean): number[] {
  const bp = Math.max(1, Math.round(Number(big(bustPpm ?? "0") / 100n)));
  const half = Math.max(1, Math.round(bp / 2));
  const steps = [-bp, -half, half, bp, bp * 2, bp * 4].map((step) => (isLong ? step : -step));
  return [...new Set(steps)].sort((a, b) => a - b);
}

function Payoff({ scenarios, margin }: { scenarios: Scenario[]; margin: string | null | undefined }) {
  const marginRaw = big(margin);
  if (!scenarios.length || marginRaw === 0n) return null;
  const top = scenarios.reduce((most, s) => {
    const value = big(s.payoutPnl);
    const abs = value < 0n ? -value : value;
    return abs > most ? abs : most;
  }, marginRaw);
  return (
    <div className="tk-payoff" aria-label="Payout at each move">
      <div className="tk-payoff-head">
        <span className="label">If the price moves</span>
        <span className="label">you get</span>
      </div>
      {scenarios.map((s) => {
        const value = big(s.payoutPnl);
        const raw = big(s.rawPnl);
        const width = top === 0n ? 0 : Number(((value < 0n ? -value : value) * 1000n) / top) / 10;
        const cut = raw > value && value > 0n ? raw - value : 0n;
        return (
          <div key={s.moveBp} className="tk-payoff-row" title={cut > 0n ? `Before impact and fee ${pnl(raw)}` : undefined}>
            <span className="num tk-move">{fmt(fromRaw(BigInt(s.moveBp), 2), { places: 2, sign: true, suffix: "%" })}</span>
            <span className="tk-bar">
              <span className={`tk-bar-fill ${value >= 0n ? "up" : "down"}`} style={{ width: `${width}%` }} />
            </span>
            <span className={`num tk-pay ${tone(value)}`}>{s.liquidated ? "bust" : pnl(value)}</span>
          </div>
        );
      })}
    </div>
  );
}

function LeverageDial({ leverage, max, onChange }: { leverage: number; max: number; onChange: (value: number) => void }) {
  const [text, setText] = useState(String(leverage));
  useEffect(() => setText(String(leverage)), [leverage]);
  const commit = (value: string) => {
    const n = Number.parseInt(value, 10);
    if (Number.isFinite(n)) onChange(Math.max(1, Math.min(max, n)));
    else setText(String(leverage));
  };
  const position = toSlider(leverage, max);
  return (
    <div className="tk-lev">
      <div className="field-head">
        <span className="label">Leverage</span>
        <span className="label">up to {max}×</span>
      </div>
      <div className="tk-lev-row">
        <button type="button" className="tk-step" onClick={() => onChange(Math.max(1, leverage - (leverage > 100 ? 10 : 1)))} aria-label="Less leverage">
          −
        </button>
        <label className="tk-lev-value">
          <span className="sr-only">Leverage</span>
          <input
            className="num"
            inputMode="numeric"
            value={text}
            onChange={(event) => setText(event.target.value.replace(/\D/g, "").slice(0, 4))}
            onBlur={(event) => commit(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && commit((event.target as HTMLInputElement).value)}
          />
          <span>×</span>
        </label>
        <button type="button" className="tk-step" onClick={() => onChange(Math.min(max, leverage + (leverage >= 100 ? 10 : 1)))} aria-label="More leverage">
          +
        </button>
      </div>
      <input
        type="range"
        className="tk-slider"
        min={0}
        max={1000}
        step={1}
        value={position}
        style={{ ["--fill" as string]: `${position / 10}%` }}
        aria-label="Leverage"
        aria-valuetext={`${leverage}×`}
        onChange={(event) => onChange(fromSlider(Number(event.target.value), max))}
      />
      <div className="chips tk-presets">
        {presets(max).map((step) => (
          <button key={step} type="button" className="chip" aria-pressed={step === leverage} onClick={() => onChange(step)}>
            {step}×
          </button>
        ))}
      </div>
    </div>
  );
}

export function Ticket({ instrument, onSent }: { instrument: Instrument; onSent?: () => void }) {
  const { status } = useConnection();
  const owner = useOwner();
  const account = useAccount();
  const stream = useStreamStatus();
  const keyState = useKeyState();
  const invalidateKey = useInvalidateKey();
  const mark = useMark(instrument.id);
  const summary = useSummary().data;
  const state = useStore(ticketStore);
  const [busy, setBusy] = useState(false);
  const marginRef = useRef<HTMLInputElement>(null);

  // each market keeps its own leverage, within its cap
  useEffect(() => {
    setTicket({ leverage: leverageFor(instrument.symbol, instrument.maxLeverage) });
  }, [instrument.symbol, instrument.maxLeverage]);

  const queued = big(account?.funds.availableQueued);
  const useDebt = state.useDebt && queued > 0n;

  const base = useMemo(
    () =>
      pt.ticket({
        owner,
        instrumentId: instrument.id,
        isLong: state.side === "long",
        margin: state.margin,
        leverage: state.leverage,
        useDebt,
        mark: mark?.raw ?? null,
        movesBp: [],
      }),
    // the preview reads the account and trading state the SDK holds; these say when they moved
    [owner, instrument, state.side, state.margin, state.leverage, useDebt, mark?.raw, account],
  );
  const moves = ladder(base.bustPpm, state.side === "long");
  const view: TicketPreview = useMemo(
    () =>
      pt.ticket({
        owner,
        instrumentId: instrument.id,
        isLong: state.side === "long",
        margin: state.margin,
        leverage: state.leverage,
        useDebt,
        mark: mark?.raw ?? null,
        movesBp: moves,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, moves.join(",")],
  );

  useEffect(() => {
    previewStore.set({ instrumentId: instrument.id, preview: view });
  }, [instrument.id, view]);
  useEffect(() => () => previewStore.set(null), []);

  const setLeverage = (leverage: number) => {
    setTicket({ leverage });
    rememberLeverage(instrument.symbol, leverage);
  };

  // L and S pick the side, [ and ] step through the presets, / jumps to the margin
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      const steps = presets(instrument.maxLeverage);
      const current = ticketStore.get().leverage;
      switch (event.key.toLowerCase()) {
        case "l":
          setTicket({ side: "long" });
          break;
        case "s":
          setTicket({ side: "short" });
          break;
        case "[": {
          const lower = [...steps].reverse().find((step) => step < current);
          if (lower) setLeverage(lower);
          break;
        }
        case "]": {
          const higher = steps.find((step) => step > current);
          if (higher) setLeverage(higher);
          break;
        }
        case "/":
          event.preventDefault();
          marginRef.current?.focus();
          break;
        default:
          return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrument.symbol, instrument.maxLeverage]);

  const scale = instrument.priceScale;
  const available = big(view.available);
  const maximum = big(view.limits?.maximum);
  const setFraction = (num: bigint, den: bigint) => {
    const amount = ((maximum * num) / den / CENT) * CENT;
    setTicket({ margin: amount > 0n ? fromRaw(amount, 18) : "" });
  };

  const isLong = state.side === "long";
  const sideWord = isLong ? "Long" : "Short";
  const notional = big(view.notional);
  const size = mark && notional > 0n ? (notional * big(scale)) / big(mark.raw) : 0n;
  const mintRate = summary?.paper.mintRate;
  const mintIfBust = mintRate && big(view.margin) > 0n ? pt.paperForLoss(view.margin as string, mintRate) : null;
  const limited = view.limits && view.limits.limit !== "collateral" && view.limits.limit !== "queueLiquidity" && maximum < available;

  const send = async () => {
    if (busy || view.refusal) return;
    setBusy(true);
    const label = `${sideWord} ${instrument.symbol} ${state.leverage}× · ${usd(view.margin ?? "0")}`;
    try {
      await openPosition(
        { instrumentId: instrument.id, isLong, margin: state.margin, leverage: state.leverage, useDebt, mark: mark?.raw ?? null },
        label,
      );
      onSent?.();
    } catch {
      // told by a toast
    } finally {
      setBusy(false);
    }
  };

  let action: React.ReactNode;
  if (status !== "connected") {
    action = <ConnectButton size="lg" block label="Connect wallet to trade" />;
  } else if (!account) {
    action = (
      <Button size="lg" block disabled busy={stream !== "live"}>
        Loading your account
      </Button>
    );
  } else if (keyState.data !== "ready") {
    action = (
      <Button
        tone="primary"
        size="lg"
        block
        busy={busy || keyState.isLoading}
        onClick={async () => {
          setBusy(true);
          try {
            await enableTrading();
          } catch {
            // told by a toast
          } finally {
            setBusy(false);
            void invalidateKey();
          }
        }}
      >
        Enable one-click trading
      </Button>
    );
  } else if (!account.hasDeposited && available === 0n && queued === 0n) {
    action = (
      <Button tone="primary" size="lg" block onClick={() => navigate("/portfolio")}>
        Deposit USDC to start
      </Button>
    );
  } else {
    action = (
      <Button tone={isLong ? "up" : "down"} size="lg" block busy={busy} disabled={view.refusal !== null} onClick={send}>
        {view.refusal ? REFUSAL[view.refusal] : `${sideWord} ${instrument.symbol} · ${state.leverage}×`}
      </Button>
    );
  }

  const closed = !instrument.openable;

  return (
    <div className="tk">
      <Segmented<Side>
        label="Side"
        size="lg"
        value={state.side}
        onChange={(side) => setTicket({ side })}
        options={[
          { value: "long", label: "Long", tone: "up" },
          { value: "short", label: "Short", tone: "down" },
        ]}
      />

      {closed && <p className="note note-warn">{instrument.symbol} is close-only: no new positions.</p>}

      <div className="field">
        <div className="field-head">
          <span className="label">Margin</span>
          {account && (
            <button type="button" className="link-btn" onClick={() => setFraction(1n, 1n)} title="Use the most this ticket allows">
              {useDebt ? "Queued" : "Available"} {usd(useDebt ? queued : available)}
            </button>
          )}
        </div>
        <AmountInput
          label="Margin in dollars"
          value={state.margin}
          onChange={(margin) => setTicket({ margin })}
          prefix="$"
          suffix="USDC"
          places={6}
          inputRef={marginRef}
          onEnter={() => void send()}
        />
        <div className="chips">
          {(
            [
              ["10%", 1n, 10n],
              ["25%", 1n, 4n],
              ["50%", 1n, 2n],
              ["Max", 1n, 1n],
            ] as const
          ).map(([label, num, den]) => (
            <button key={label} type="button" className="chip" disabled={maximum === 0n} onClick={() => setFraction(num, den)}>
              {label}
            </button>
          ))}
        </div>
        {queued > 0n && (
          <Segmented<"balance" | "queued">
            label="Fund from"
            size="sm"
            value={useDebt ? "queued" : "balance"}
            onChange={(source) => setTicket({ useDebt: source === "queued" })}
            options={[
              { value: "balance", label: `Balance ${usd(available, { compact: 5 })}` },
              { value: "queued", label: `Queued ${usd(queued, { compact: 5 })}` },
            ]}
          />
        )}
        {limited && view.limits && (
          <span className="label warn">
            Max {usd(view.limits.maximum)} at {state.leverage}× ({view.limits.limit === "openInterest" ? "side near its OI cap" : view.limits.limit === "marketMaxSize" ? "market max size" : "leverage cap"})
          </span>
        )}
      </div>

      <LeverageDial leverage={state.leverage} max={instrument.maxLeverage} onChange={setLeverage} />

      <div className="tk-bust">
        <div>
          <div className="label">Liquidation</div>
          <div className="num tk-bust-price">{view.bust ? price(view.bust, scale) : "—"}</div>
        </div>
        <div className="tk-bust-dist">
          <div className="label">away</div>
          <div className={`num ${big(view.bustPpm) < 2_000n ? "down" : big(view.bustPpm) < 10_000n ? "warn" : ""}`}>
            {view.bustPpm ? pctPpm(view.bustPpm) : "—"}
          </div>
        </div>
      </div>

      <div className="tk-summary">
        <div className="kv">
          <span className="kv-label">Position size</span>
          <span className="kv-value num">
            {notional > 0n ? usd(notional) : "—"}
            {size > 0n && <span className="faint"> · {fmt(fromRaw(size, 18), { places: scalePlaces(scale) + 3 })} {instrument.symbol}</span>}
          </span>
        </div>
        <div className="kv">
          <span className="kv-label">Entry at mid</span>
          <span className="kv-value num">{mark ? price(mark.raw, scale) : "—"}</span>
        </div>
        <div className="kv" title="Winning closes pay after the venue's impact curve and a win fee. Losses pay no fee.">
          <span className="kv-label">Fees</span>
          <span className="kv-value num">
            {view.winFee ? `${fmt(fromRaw(big(view.winFee) * 100n, 18), { places: 2 })}% of wins` : "—"}
            <span className="faint"> · none on losses</span>
          </span>
        </div>
        {mintIfBust && (
          <div className="kv" title="Every dollar lost mints PAPER, which you can stake for USDC.">
            <span className="kv-label">If liquidated</span>
            <span className="kv-value num">+{paper(mintIfBust)} PAPER</span>
          </div>
        )}
      </div>

      <Payoff scenarios={view.scenarios ?? []} margin={view.margin} />

      <div className="tk-action">{action}</div>
      {status === "connected" && keyState.data !== "ready" && account && (
        <p className="tk-hint">One signature, no gas. A 30-day session key in this browser trades for you and can never withdraw.</p>
      )}
      <p className="tk-hint tk-keys">
        <kbd>L</kbd> <kbd>S</kbd> side · <kbd>[</kbd> <kbd>]</kbd> leverage · <kbd>/</kbd> margin
      </p>
    </div>
  );
}
